import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import Reveal from '../../components/Reveal';
import Icon from '../../components/Icon';
import { Button, Field } from '../../components/ui';
import { useToast } from '../../components/Toast';
import usePageTitle from '../../hooks/usePageTitle';

export default function Contact() {
  const { t } = useTranslation();
  const toast = useToast();
  usePageTitle(t('contact.pageTitle'));
  const topics = t('contact.topics', { returnObjects: true });
  const [form, setForm] = useState({ name: '', email: '', phone: '', topic: 'family', message: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setFieldErrors({});
    try {
      await api.post('/api/contact', form);
      setSent(true);
    } catch (err) {
      toast.error(err.message);
      setFieldErrors(err.fieldErrors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="page-hero">
        <div className="shell">
          <Reveal>
            <span className="eyebrow">{t('contact.eyebrow')}</span>
            <h1>{t('contact.title')}</h1>
            <p className="lede">{t('contact.lede')}</p>
          </Reveal>
        </div>
      </section>

      <section className="band">
        <div className="shell">
          <div className="contact-layout">
            <Reveal className="contact-form-card">
              {sent ? (
                <div className="sent-state">
                  <span className="sent-tick"><Icon name="check" size={28} strokeWidth={2} /></span>
                  <h2>{t('contact.sentTitle')}</h2>
                  <p>{t('contact.sentBody')}</p>
                  <div className="hero-actions">
                    <Link className="btn btn-primary" to="/">{t('contact.backToHome')}</Link>
                    <button type="button" className="btn btn-outline" onClick={() => { setSent(false); setForm({ ...form, message: '' }); }}>
                      {t('contact.sendAnother')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h2>{t('contact.formTitle')}</h2>
                  <form onSubmit={submit} noValidate>
                    <Field label={t('contact.yourName')} required error={fieldErrors.name}>
                      <input value={form.name} onChange={set('name')} autoComplete="name" required />
                    </Field>
                    <div className="grid-2">
                      <Field label={t('contact.emailAddress')} required error={fieldErrors.email}>
                        <input type="email" value={form.email} onChange={set('email')} autoComplete="email" required />
                      </Field>
                      <Field label={t('contact.phoneNumber')} error={fieldErrors.phone}>
                        <input value={form.phone} onChange={set('phone')} autoComplete="tel" />
                      </Field>
                    </div>
                    <Field label={t('contact.whatIsItAbout')} required>
                      <select value={form.topic} onChange={set('topic')}>
                        {topics.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t('contact.message')} required error={fieldErrors.message}>
                      <textarea rows={6} value={form.message} onChange={set('message')} required />
                    </Field>
                    <Button type="submit" loading={busy}>{t('contact.sendMessage')}</Button>
                  </form>
                </>
              )}
            </Reveal>

            <Reveal className="contact-side" delay={120}>
              <div className="info-card">
                <h3>{t('contact.alreadyWithUs')}</h3>
                <p>{t('contact.alreadyWithUsBody')}</p>
                <Link className="btn btn-secondary btn-block" to="/login">{t('contact.signIn')}</Link>
              </div>
              <div className="info-card">
                <h3>{t('contact.needNannySoon')}</h3>
                <p>{t('contact.needNannySoonBody')}</p>
                <Link className="btn btn-outline btn-block" to="/register/family">{t('contact.requestNanny')}</Link>
              </div>
              <div className="info-card">
                <h3>{t('contact.commonQuestions')}</h3>
                <p>{t('contact.commonQuestionsBody')}</p>
                <Link className="btn btn-outline btn-block" to="/faq">{t('contact.readFaq')}</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
